import { TypedDocumentNode, gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { Navigate } from 'react-router-dom';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DisplayS } from '@sorare/core/src/atoms/typography';
import { Countdown } from '@sorare/core/src/components/Countdown';
import { ANY_SPORT_MARKET } from '@sorare/core/src/constants/__generated__/routes';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCurrentSport } from '@sorare/core/src/hooks/useCurrentSport';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';
import { useTimeLeft } from '@sorare/core/src/hooks/useTimeLeft';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { Logo } from '../Logo';
import { Snow } from '../Snow';
import backgroundUrl from '../assets/background.jpg';
import {
  NEON_CHRISTMAS_EVENTS,
  NEON_CHRISTMAS_START_DATE,
  getAdventCalendarDimensions,
} from '../event';
import { CalendarDay } from './CalendarDay';
import {
  AdventCalendarQuery,
  AdventCalendarQueryVariables,
} from './__generated__/index.graphql';

const Wrapper = styled(Vertical)`
  position: relative;
  isolation: isolate;
  background-image: url(${backgroundUrl});
  background-position: center top;
  background-size: cover;
  flex: 1;
`;

const CalendarGrid = styled.div`
  flex: 1;
  display: grid;
  grid-auto-flow: dense;
  gap: var(--gap);
  grid-template-columns: repeat(var(--cols), 1fr);
  grid-template-rows: repeat(var(--rows), 1fr);
  margin: var(--double-unit);
`;

const Close = styled(IconButton).attrs({ icon: faTimes, color: 'tertiary' })`
  position: absolute;
  left: var(--double-unit);
  top: var(--double-unit);
`;

const ADVENT_CALENDAR_QUERY = gql`
  query AdventCalendarQuery {
    tokens {
      footballPromotionalEvents: promotionalEvents(sport: FOOTBALL) {
        slug
        ...CalendarDay_PromotionalEvent
      }
      nbaPromotionalEvents: promotionalEvents(sport: NBA) {
        slug
        ...CalendarDay_PromotionalEvent
      }
    }
  }
  ${CalendarDay.fragments.PromotionalEvent}
` as TypedDocumentNode<AdventCalendarQuery, AdventCalendarQueryVariables>;

export const AdventCalendar = () => {
  const {
    flags: { useNeonChristmas = false },
  } = useFeatureFlags();
  const sport = useCurrentSport();

  const { isEnded } = useTimeLeft(NEON_CHRISTMAS_START_DATE);

  const { data } = useQuery(ADVENT_CALENDAR_QUERY);
  let todayPromotionalEvent =
    data?.tokens.footballPromotionalEvents[0] ??
    data?.tokens.nbaPromotionalEvents[0];
  if (
    todayPromotionalEvent?.startDate.getFullYear() !== 2024 ||
    // month are 0 indexed
    todayPromotionalEvent?.startDate.getUTCMonth() !== 11
  ) {
    todayPromotionalEvent = undefined;
  }

  const [ref, { width, height }] = useMeasure<HTMLDivElement>();
  const isTabletAndAbove = useIsTabletAndAbove();

  if (!useNeonChristmas) {
    return <Navigate to={generateSportPath(ANY_SPORT_MARKET, { sport })} />;
  }
  const [rows, cols] = isTabletAndAbove
    ? getAdventCalendarDimensions(width, height)
    : [10, 3];

  return (
    <Wrapper>
      <Close to={generateSportPath(ANY_SPORT_MARKET, { sport })} />
      <Snow />
      <Logo style={{ alignSelf: 'center' }} />
      {!isEnded && (
        <Countdown
          date={NEON_CHRISTMAS_START_DATE}
          greyOutZeros
          gap={2}
          Label={DisplayS}
        />
      )}
      <CalendarGrid
        ref={ref}
        style={
          {
            '--rows': rows,
            '--cols': cols,
            '--gap': 'var(--double-unit)',
          } as React.CSSProperties
        }
      >
        {NEON_CHRISTMAS_EVENTS.map(e => (
          <CalendarDay
            key={e.day}
            style={
              e.area % 2
                ? {
                    // odd areas spread horizontally
                    gridColumn: `span ${e.area}`,
                  }
                : {
                    // even areas spread take 2 rows
                    gridColumn: `span ${e.area / 2}`,
                    gridRow: `span ${2}`,
                  }
            }
            dailyEvent={e}
            event={todayPromotionalEvent}
          />
        ))}
      </CalendarGrid>
    </Wrapper>
  );
};
